import { useCallback } from 'react';
import useDownloadSize from './stateful/useDownloadSize';
import useOriginalSize from '../shared/hooks/useOriginalSize';
import { DownloadRequestBody } from '../../types/isomorphic';
import {
  useDownloadData,
  useDownloadMutation,
  useFailDownload,
} from '../shared/api/download/downloadModifiedQueries';
import useDownloadModifiedAsset from './useDownloadModifiedAsset';
import isCompleteDownloadRequest from '../utils/typeGuards';

const useModifiedDownload = () => {
  // genId/index are typed nullable on the shared context (a plain, unmodified
  // asset has neither), but this hook is only used within the modified-download
  // flow, where both are always resolved by the time this hook runs.
  const { genId: genRequestId, index } = useDownloadModifiedAsset();

  const { downloadSize } = useDownloadSize();
  const { originalSize } = useOriginalSize();

  const { mutate: createDownloadRequest } = useDownloadMutation();
  const failDownload = useFailDownload();
  const downloadData = useDownloadData({
    genRequestId,
    index,
  });

  const onDownload = useCallback(
    (options?: DownloadRequestBody) => {
      const { projectCode, notes } = options || {};
      createDownloadRequest({
        genRequestId: genRequestId as string,
        index: index as number,
        // downloadSize?.sizeName, matching ModifiedDownloadButton.jsx's sibling
        // guard: LicensedModifiedSize's fibo path can seed an undefined size
        // while /download-sizes is still in flight.
        sizeName: downloadSize?.sizeName,
        // TODO(backend): confirm the request endpoint accepts originalSizeName
        // and wires the original-asset download/purchase (epic 86b8u87x4).
        originalSizeName: originalSize?.name || undefined,
        projectCode,
        notes,
      });
    },
    [createDownloadRequest, downloadSize, originalSize, index, genRequestId]
  );

  return {
    modifiedAssetDownloadData: downloadData,
    downloadIdKey: isCompleteDownloadRequest(downloadData)
      ? downloadData?.downloadId
      : undefined,
    downloadModifiedAsset: onDownload,
    markModifiedDownloadFailed: () =>
      failDownload({
        genRequestId: genRequestId as string,
        index: index as number,
      }),
  };
};

export default useModifiedDownload;
